import React, { useState, useEffect } from 'react';
import {
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonModal,
  IonTitle,
  IonToolbar,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { ExploreElementType } from '../../spinnaxBase/data/explore';
import { ExploreElement } from '../../models/ExploreElementModel/ExploreElementModel';
import Trans from 'util/Trans';
import './EditElementModal.css';
import DynamicButton from 'components/buttons/DynamicButton';
import { logger } from 'services/Logger';
import { present } from '@ionic/core/dist/types/utils/overlays';

interface EditElementModalProps {
  isOpen: boolean;
  onDidDismiss: () => void;
  onUpdateElement: (elementData: any) => Promise<void>;
  element: ExploreElement | null;
}

const EditElementModal: React.FC<EditElementModalProps> = ({
  isOpen,
  onDidDismiss,
  onUpdateElement,
  element,
}) => {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    type: ExploreElementType.Event,
    date: new Date().toISOString().split('T')[0],
    startDate: '',
    endDate: '',
    country: '',
    location: '',
    description: '',
    isPublic: true,
  });

  const [isLoading, setIsLoading] = useState(false);

  // Lade Daten vom Element wenn geöffnet
  useEffect(() => {
    if (isOpen && element) {
      // Sichere Date-Konvertierung für startDate und endDate
      let startDateValue = '';
      let endDateValue = '';
      if (element.startDate) {
        try {
          const parsedStart = new Date(element.startDate);
          if (!isNaN(parsedStart.getTime())) {
            // datetime-local Format: "YYYY-MM-DDTHH:MM"
            startDateValue = parsedStart.toISOString().slice(0, 16);
          }
        } catch (error) {
          logger.warn('Invalid startDate format:', element.startDate);
        }
      }
      if (element.endDate) {
        try {
          const parsedEnd = new Date(element.endDate);
          if (!isNaN(parsedEnd.getTime())) {
            endDateValue = parsedEnd.toISOString().slice(0, 16);
          }
        } catch (error) {
          logger.warn('Invalid endDate format:', element.endDate);
        }
      }

      setFormData({
        id: element.id,
        name: element.name,
        type: element.type as ExploreElementType,
        date: startDateValue || new Date().toISOString().split('T')[0],
        startDate: startDateValue,
        endDate: endDateValue,
        country: element.country || '', // Kein 'missing' mehr
        location: element.location || '', // Kein 'missing' mehr
        description: element.description || '',
        isPublic: (element as any).isPublic ?? true,
      });
    }
  }, [isOpen, element]);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert('Name ist erforderlich');
      return;
    }
    // Validate future date for events
    if (formData.type === ExploreElementType.Event && formData.startDate) {
      const selectedDate = new Date(formData.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        alert(
          'Events müssen in der Zukunft liegen. Bitte wählen Sie ein zukünftiges Datum.'
        );
        return;
      }
    }
    setIsLoading(true);
    try {
      let startDate = null;
      let endDate = null;
      if (formData.startDate) {
        try {
          const parsedDate = new Date(formData.startDate);
          if (!isNaN(parsedDate.getTime())) {
            startDate = parsedDate;
          }
        } catch (error) {
          logger.error(
            'Invalid startDate format in form data:',
            formData.startDate
          );
        }
      }
      if (formData.endDate) {
        try {
          const parsedDate = new Date(formData.endDate);
          if (!isNaN(parsedDate.getTime())) {
            endDate = parsedDate;
          }
        } catch (error) {
          logger.warn('Invalid endDate format in form data:', formData.endDate);
        }
      }
      await onUpdateElement({
        ...formData,
        isPublic:
          formData.type === ExploreElementType.Ranking
            ? true
            : formData.isPublic,
        startDate,
        endDate,
      });
      onDidDismiss();
    } catch (error) {
      logger.error('Error updating element:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onDidDismiss}
      className="edit-modal"
    >
      <IonHeader className="ion-no-border">
        <IonToolbar
          style={{
            background: 'transparent',
          }}
        >
          <IonButtons slot="start">
            {isLoading ? (
              <IonSpinner name="crescent" color="light" />
            ) : (
              <DynamicButton
                text={
                  <Trans i18nKey="UPDATE_ELEMENT">Element aktualisieren</Trans>
                }
                iconName="OKUpIcon"
                buttonType="icon-full"
                size="xlarge"
                fill="clear"
                className="spnx-button"
                onClick={handleSubmit}
                disabled={isLoading}
              />
            )}
          </IonButtons>
          <IonButtons slot="end">
            <DynamicButton
              text={<Trans i18nKey="CANCEL" />}
              iconName="CancelUpIcon"
              buttonType="icon-full"
              size="xlarge"
              fill="clear"
              className="spnx-button"
              onClick={onDidDismiss}
            />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent
        style={{ '--background': 'transparent', '--border-colour': 0 }}
      >
        <div className="edit-modal-wrapper">
          <div className="edit-modal-group">
            <IonText>
              <Trans i18nKey="BASIC_INFORMATION">Grundinformationen</Trans>
            </IonText>
            <div className="edit-modal-innergroup">
              <IonItem>
                <IonLabel position="stacked">Name *</IonLabel>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Namen eingeben..."
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="DESCRIPTION">Beschreibung</Trans>
                </IonLabel>
                <textarea
                  value={formData.description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Beschreibung eingeben..."
                  rows={3}
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </IonItem>
            </div>
          </div>
          <div className="edit-modal-group">
            <IonText>
              <Trans i18nKey="DATE_INFORMATION">Datum</Trans>
            </IonText>
            <div className="edit-modal-innergroup">
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="START_DATE">Startdatum</Trans>
                </IonLabel>
                <input
                  type="datetime-local"
                  value={formData.startDate || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, startDate: e.target.value })
                  }
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="END_DATE">Enddatum</Trans>
                </IonLabel>
                <input
                  type="datetime-local"
                  value={formData.endDate || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, endDate: e.target.value })
                  }
                />
              </IonItem>
            </div>
          </div>
          <div className="edit-modal-group">
            <IonText>
              <Trans i18nKey="LOCATION_INFORMATION">
                Standortinformationen
              </Trans>
            </IonText>
            <div className="edit-modal-innergroup">
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="COUNTRY">Land</Trans>
                </IonLabel>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, country: e.target.value })
                  }
                  placeholder="Land eingeben..."
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="LOCATION">Ort</Trans>
                </IonLabel>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, location: e.target.value })
                  }
                  placeholder="Ort eingeben..."
                />
              </IonItem>
            </div>
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default EditElementModal;
